import Layout from "modules/layout/dashboard"
import { useInvoiceId, useMemberId } from "pages/member"
import { useMember } from "pages/member/queries"
import { useInvoice, usePayInvoice, usePaymentMethods } from "../../queries"
import {
  Button,
  Card,
  Col,
  Descriptions,
  message,
  Result,
  Row,
  Space,
  Typography,
} from "antd"
import { useTranslation } from "react-i18next"
import { CreditCard, PaymentForm } from "react-square-web-payments-sdk"
import { Link, useNavigate } from "react-router-dom"
import { useEffect } from "react"
import moment from "moment"
import { ReactComponent as DownloadIcon } from "assets/icons/download.svg"
import Loader from "components/loader"
import styles from "./styles.module.scss"

const { Main } = Layout
const { Title, Text } = Typography

const squareAppId = String(import.meta.env.VITE_APP_SQUARE_APP_ID ?? "").trim()
const squareLocationId = String(
  import.meta.env.VITE_APP_SQUARE_LOCATION_ID ?? ""
).trim()

const CreditCardPay: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const memberId = useMemberId()
  const invoiceId = useInvoiceId()
  const member = useMember(memberId)
  const invoice = useInvoice(memberId!, invoiceId!)
  const paymentMethods = usePaymentMethods()
  const [messageApi, contextHolder] = message.useMessage()
  const { mutate, isLoading, isSuccess } = usePayInvoice(invoiceId!, {
    onSuccess: () => {
      messageApi.success({
        key: "paying",
        content: t("invoice.payment_success"),
      })
    },
    onError: () => {
      messageApi.error({
        key: "paying",
        content: t("invoice.payment_failed"),
      })
    },
  })

  useEffect(() => {
    invoice.data &&
      ["paid", "recoverable", "canceled"].includes(invoice.data?.status!) &&
      navigate("../..")
  }, [invoice.data])

  if (member.isLoading || invoice.isLoading || paymentMethods.isLoading) {
    return <Loader />
  }

  return (
    <>
      {contextHolder}
      <Main.PageHeader
        breadcrumbs={[
          {
            title: member.data?.name,
            link: `/members/${member.data?.id}/events`,
          },
          {
            title: t("invoice.invoice"),
            link: `/members/${member.data?.id}/invoices`,
          },
          {
            title:
              invoice.data?.reference ??
              invoice.data?.proforma_reference ??
              "-",
            link: `/members/${member.data?.id}/invoices/${invoice.data?.id}`,
          },
          {
            title: t("invoice.payment"),
            link: `/members/${member.data?.id}/invoices/${invoice.data?.id}/payment`,
          },
          {
            title: t("invoice.text_1"),
          },
        ]}
        onButtonClick={() =>
          navigate(`/members/${memberId}/invoices/${invoiceId}/payment`)
        }
        title={
          <Title level={3}>
            {t("invoice.text_1", {
              entity:
                invoice.data?.reference ??
                invoice.data?.proforma_reference ??
                "-",
            })}
          </Title>
        }
      />
      {!paymentMethods.data?.some(({ name }) => name === "card") ? (
        <Card>
          <Result
            status="warning"
            title={t("invoice.payment_credit_card_not_allowed")}
            subTitle={t("invoice.payment_credit_card_not_allowed_desc")}
            extra={
              <Link to="..">
                <Button type="primary">{t("invoice.back_to_payment")}</Button>
              </Link>
            }
          />
        </Card>
      ) : (
        <Card style={{ width: "100%" }}>
          {isSuccess ? (
            <Result
              status="success"
              title={t("invoice.payment_success")}
              extra={
                <Button
                  type="primary"
                  onClick={() =>
                    window.open(
                      new URL(
                        `operation/administration/members/${memberId}/invoices/${invoiceId}/export`,
                        import.meta.env.VITE_APP_API_URL
                      ).href
                    )
                  }
                >
                  <Space align="center">
                    <DownloadIcon />
                    {t("global.download")}
                  </Space>
                </Button>
              }
            />
          ) : (
            <Row>
              <Col md={11} sm={24}>
                <Descriptions column={1}>
                  <Descriptions.Item label={t("members.member")}>
                    <Text strong>{member.data?.name} </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label={t("invoice.at_the_command_of")}>
                    <Text strong>{invoice.data?.recipient.name} </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label={t("invoice.due_date")}>
                    <Text strong>
                      {moment(invoice.data?.due_date).format("DD-MM-YYYY")}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label={t("invoice.balance")}>
                    <Text strong>{invoice.data?.balance}</Text>
                  </Descriptions.Item>
                </Descriptions>
              </Col>
              <span className={styles.divider} />
              <Col md={11} sm={24}>
                <PaymentForm
                  applicationId={squareAppId}
                  locationId={squareLocationId}
                  cardTokenizeResponseReceived={async (token: any) => {
                    mutate({ source_id: token.token })
                    messageApi.loading({
                      key: "paying",
                      content: t("global.processing"),
                      duration: 0,
                    })
                  }}
                >
                  <CreditCard
                    buttonProps={{
                      isLoading,
                    }}
                  >
                    {t("invoice.paye")}
                  </CreditCard>
                </PaymentForm>
              </Col>
            </Row>
          )}
        </Card>
      )}
    </>
  )
}
export default CreditCardPay
